import { UserRoleEnum } from '../enums/UserRoleEnum';
import { UsersOrderColumnEnum } from '../enums/UsersOrderColumnEnum';
import type User from './User';

// Payload for retrieving a list of users, with optional filters and sorting.
export interface GetUsersPayload {
  offset: number; // Starting index for pagination.
  count: number; // Number of users to return.
  orderColumn?: UsersOrderColumnEnum; // Optional column to sort by.
  orderAsc?: boolean; // Sort ascending? If omitted, descending.
  search?: string; // Search term for filtering users.
  shareFilter?: number; // Filter by share criteria.
  statusFilter?: number[]; // Array of statuses to filter users.
  roleFilter?: UserRoleEnum[]; // Array of roles to filter users.
  /** Exclude users with active access on any of these file/folder ids. */
  excludeFileIds?: number[];
  ignoreBehalf?: boolean; // If true, ignore "on behalf of" filters.
  onBehalfOf?: number; // Restrict results to "on behalf of" ID.
  groupId?: number; // Restrict results to members of this group.
  externalProvider?: string; // Restrict results to provider (e.g. 'google').
}

// Response for user list endpoints.
export interface GetUsersResponse {
  users: User[]; // Returned list of users.
  total: number; // Total count of all matching users.
}

// Payload for creating a new user.
export interface CreateUserPayload {
  firstName: string; // User's first name.
  lastName: string; // User's last name.
  email: string; // User's email address.
  role: UserRoleEnum; // Role assigned to the user.
  expirationDate: number; // Expiration timestamp for the account.
  onBehalfOf?: number; // (Optional) Registered "on behalf of" entity/user.
  privateUser: boolean; // If true, account is private.
  excludeFromSso?: boolean; // If true, user must log in via OTP even if SSO whitelist applies.
  /**
   * Optional login alias. Omit/null/"" = no alias on create; non-empty string = set.
   */
  loginAlias?: string | null;
}

// Details for a supported OAuth provider.
export interface OAuthProvider {
  provider: 'microsoft' | 'google'; // OAuth provider type.
  providerName: string; // Display name for UI.
  authorizationEndpoint: string; // OAuth endpoint for authorization code.
  clientId: string; // App/client ID for OAuth.
  scopes: string[]; // Scopes to request.
  tenantId?: string; // (Microsoft only) tenant identifier.
}

// Response indicating available authentication mechanisms (OTP/Direct OAuth).
export interface GetUserOtpResponse {
  useDirectOAuth?: boolean; // True if direct OAuth is offered.
  oauthProviders?: OAuthProvider[]; // Available OAuth providers, if any.
}

// Request payload for entering on behalf of a user.
export interface EnterOnBehalfOfUserPayload {
  targetUserId: number; // ID of the user to impersonate.
  reason?: string; // Optional string explaining the need.
}

// Response for entering on behalf of a user (impersonation).
export interface EnterOnBehalfOfUserResponse {
  token: string; // Impersonation token.
  rToken: string; // Refresh token.
  impersonatedUserId: number; // ID of the user impersonated.
  impersonatedUserEmail: string; // Email of the impersonated user.
  impersonatedUserName: string; // Name of the impersonated user.
  adminUserId: number; // The admin's user ID.
  expiresAt: number; // Expiration time of impersonation session.
  /** True if notification email was queued; false if not; omit on older backends. */
  impersonatedUserNotified?: boolean;
}

// Response after exiting on behalf-of mode.
export interface ExitOnBehalfOfUserResponse {
  token: string; // Admin's original session token (restored).
  rToken: string; // Refresh token for the same.
}

// Response after setting up TOTP.
export interface TotpSetupResponse {
  secret: string; // TOTP secret for user's authenticator app.
  uri: string; // Standard otpauth URI for QR codes, etc.
  account: string; // Account label for TOTP, e.g., user's email.
  issuer: string; // Issuing authority/organization label.
}

// 403 TOTP_SETUP_REQUIRED body from `POST /user/auth/v2` (login blocked: TOTP policy grace period elapsed).
export interface TotpSetupRequiredErrorBody {
  partialAuthToken: string; // 5-minute token authorizing the forced setup endpoints below.
  expiresAt: string; // ISO datetime when partialAuthToken expires.
  userEmail: string; // Masked email for display, e.g. "va***@ex***.com".
}

// Payload to start forced TOTP setup with a partial auth token (no Bearer session).
export interface TotpForcedSetupPayload {
  partialAuthToken: string;
}

// Payload to verify the forced TOTP setup code and complete auto-login.
export interface TotpForcedSetupVerifyPayload {
  partialAuthToken: string;
  code: string;
}

// Response after verifying forced TOTP setup: full session tokens plus one-time backup codes.
export interface TotpForcedSetupVerifyResponse {
  auth: { token: string; rToken: string };
  backupCodes: string[];
}

// Request payload for verifying a TOTP or backup code.
export interface TotpVerifyPayload {
  code: string; // TOTP code as entered by the user.
}

// Result of verifying TOTP or backup code.
export interface TotpVerifyResponse {
  enabled: boolean; // TOTP active on this account?
  backupCodes: string[]; // Remaining/valid backup codes.
  enabledAt: string; // ISO string for when TOTP was enabled.
}

// Details about a user's TOTP status.
export interface TotpStatusResponse {
  enabled: boolean; // Is TOTP enabled?
  enabledAt: string; // When was TOTP enabled?
  backupCodesRemaining: number; // How many backup codes remain unused.
  requiredByPolicy: boolean; // Whether TOTP is required by system policy.
  gracePeriodEnds: string; // When enforcement begins (if in grace period).
}

// Payload for disabling TOTP protection.
export interface TotpDisablePayload {
  code?: string; // Optionally require TOTP code to confirm.
}

// Payload for regenerating TOTP backup codes.
export interface TotpRegenerateBackupCodesPayload {
  code?: string; // Optionally require TOTP code to confirm.
}

// Response after regenerating backup codes.
export interface TotpRegenerateBackupCodesResponse {
  backupCodes: string[]; // New backup codes.
  count?: number; // How many codes were generated.
}

// Response for forcibly removing TOTP protection from a user.
export interface TotpRemoveFromUserResponse {
  success: boolean; // Was the removal successful?
  userId: number; // ID of the affected user.
  message: string; // Message for UI/logging.
}

// Login V2: full success response (no second factor required).
export interface LoginV2SuccessResponse {
  token: string; // Full session token.
  rToken: string; // Refresh token.
  expiration?: string; // Optional session expiry ISO string.
  rexpiration?: string; // Optional refresh expiry ISO string.
}

// Login V2: partial response, TOTP/passkey required.
export interface LoginV2TotpRequiredResponse {
  totpRequired?: true; // If true, TOTP is required for login.
  twoFactorRequired?: true; // If true, some 2FA is required.
  partialAuthToken: string; // Temporary auth token for 2FA step.
  expiresAt: string; // When this partial token expires.
  userEmail: string; // Email address for which login is attempted.
  message?: string; // Optional message for client UI.
  totpAvailable?: boolean; // False if TOTP is neither needed nor supported.
  passkeyAvailable?: boolean; // False if passkey flow is unavailable.
}

// Union type for either a full or partial login-v2 response.
export type LoginV2Response = LoginV2SuccessResponse | LoginV2TotpRequiredResponse;

/**
 * Determines if a login response requires second factor (TOTP/passkey).
 * Returns true if a partialAuthToken is present and a 2FA field is set.
 */
export function isLoginV2TwoFactorResponse(
  response: LoginV2Response,
): response is LoginV2TotpRequiredResponse {
  const r = response as LoginV2TotpRequiredResponse;
  if (!('partialAuthToken' in r) || typeof r.partialAuthToken !== 'string') {
    return false;
  }
  if ('token' in response && typeof (response as LoginV2SuccessResponse).token === 'string') {
    return false;
  }
  return r.totpRequired === true || r.twoFactorRequired === true;
}

// Represents a single passkey credential registered to a user/admin account.
export interface PasskeyCredentialDto {
  credentialId: string; // Unique credential identifier.
  deviceName: string; // User-labeled device name.
  createdAt: string; // ISO date when credential was registered.
  lastUsedAt: string | null; // ISO date or null; last usage time.
  isDiscoverable: boolean; // If credential is resident-discoverable.
  passwordlessEnabled: boolean; // If passwordless login is enabled.
  isBackupEligible: boolean; // Can be backed up (to OS/cloud)?
  isBackedUp: boolean; // Was credential successfully backed up?
  aaGuid: string; // Authenticator GUID.
  attestationFormat: string; // Attestation format used (e.g., 'none', 'packed').
  /** May be missing or a string on some API responses; normalize in UI before iterating. */
  transports?: string[] | string | null;
}

// User's passkey registration/status information.
export interface PasskeyStatusResponse {
  allowedByPolicy: boolean; // True if passkeys are permitted by policy.
  credentialCount: number; // Count of registered credentials.
  credentials: PasskeyCredentialDto[]; // List of passkey credentials.
  enabled: boolean; // Are passkeys generally enabled?
  passwordlessAvailable: boolean; // Can use passkey for passwordless login.
}

// Options needed for registering a new passkey credential.
export interface PasskeyRegisterOptionsResponse {
  options: string; // PublicKeyCredentialCreationOptions, base64.
  sessionId: string; // Session/binding ID for this registration request.
}

// Payload to complete passkey registration.
export interface PasskeyRegisterCompletePayload {
  attestationResponse: string; // Attestation object, base64-encoded.
  deviceName: string; // Name for the new device.
  sessionId: string; // Registration session ID.
  transports: string[]; // Transports declared by browser.
}

// Response when registration is successful.
export interface PasskeyRegisterCompleteResponse {
  createdAt: string; // When credential was created.
  credentialId: string; // Credential unique ID.
  deviceName: string; // User-supplied device name.
  isDiscoverable: boolean; // Resident key/discoverable?
  passwordlessEnabled: boolean; // Can use device for passwordless login?
  transports: string[]; // Usable transports.
}

// Payload needed for starting passkey authentication.
export interface PasskeyAuthOptionsPayload {
  partialAuthToken: string; // The partial auth token from login.
}

// Options returned for passkey authentication initiation.
export interface PasskeyAuthOptionsResponse {
  options: string; // PublicKeyCredentialRequestOptions, base64.
  sessionId: string; // Ongoing authentication session/binding.
}

// Payload to complete passkey authentication/challenge.
export interface PasskeyAuthCompletePayload {
  assertionResponse: string; // The signed assertion, base64.
  partialAuthToken: string; // The partial token received from login.
  sessionId: string; // Matching the challenge.
}

// Payload for requesting passwordless auth options (multi-tenant).
export interface PasswordlessOptionsPayload {
  tenantUrl: string; // Tenant origin (used in challenge).
  username?: string; // Optional username hint.
}

// Auth options/object for passwordless login.
export interface PasswordlessAuthOptionsResponse {
  options: string; // PublicKeyCredentialRequestOptions, base64.
  sessionId: string; // Challenge/session pair.
}

// Completing passwordless authentication.
export interface PasswordlessAuthCompletePayload {
  assertionResponse: string; // The assertion data (from client).
  sessionId: string; // The session identifier.
}

// Response after passkey authentication: session tokens.
export interface PasskeyAuthTokensResponse {
  companyId?: number; // Company/tenant ID (optional).
  expiration?: string; // Session login expiry.
  rexpiration?: string; // Refresh token expiry date/time.
  rToken: string; // Refresh token.
  token: string; // Session token.
  userId?: number; // User ID associated (optional).
}

// Payload for renaming a registered passkey device.
export interface PasskeyRenamePayload {
  deviceName: string; // New device name.
}

// Payload for toggling passwordless enabled/disabled on a credential.
export interface PasskeyTogglePasswordlessPayload {
  enabled: boolean; // Desired state.
}

// Response for removing a passkey credential.
export interface PasskeyRemoveResponse {
  message: string; // Response for display/logging.
  success: boolean; // Removal result.
}

// Response for generic passkey mutation (e.g., rename).
export interface PasskeyMutationResponse {
  message: string; // Response for display/logging.
  success: boolean; // Result of the mutation.
}

// Admin status response for user's registered passkeys.
export interface PasskeyAdminStatusResponse {
  credentialCount: number; // Number of credentials.
  credentials: PasskeyCredentialDto[]; // Their credential info.
  failedAttempts: number; // Number of failed attempts.
  isBlocked: boolean; // Blocked due to failures?
  passkeyEnabled: boolean; // Passkeys turned on for user.
  userId: number; // Admin-viewed user ID.
}

// Response after admin removes a credential from user.
export interface PasskeyAdminRemoveCredentialResponse {
  message: string; // Message for display/logging.
  success: boolean; // If the removal succeeded.
  userId: number; // Target user.
}

// Response after admin removes all credentials from user.
export interface PasskeyAdminRemoveAllResponse {
  message: string; // For display/logging.
  success: boolean;
  userId: number;
}

// Payload to complete a login with TOTP code or backup code.
export interface LoginWithTotpPayload {
  partialAuthToken: string; // Temporary login token.
  code: string; // User's TOTP or backup code.
  isBackupCode: boolean; // True if using backup code path.
}

// Response for successfully completing login via TOTP/backup.
export interface LoginWithTotpResponse {
  token: string; // Session token.
  rToken: string; // Refresh token.
  expiration?: string; // Session token expires at (optional).
  rexpiration?: string; // Refresh token expires at (optional).
}

// --- Admin TOTP exemption endpoints ---

// Detailed TOTP status for an individual user.
export interface TotpAdminStatusResponse {
  userId: number; // User's unique identifier.
  totpEnabled: boolean; // If TOTP is enabled.
  totpEnabledAt: string; // Date/time TOTP enabled.
  backupCodesRemaining: number; // Unused backup codes.
  exempt: boolean; // If user is currently exempt.
  exemptPermanent: boolean; // Exemption is permanent.
  exemptUntil: string | null; // Exemption expiration date.
  exemptReason: string | null; // Reason for exemption.
  exemptGrantedAt: string | null; // Date/time exemption granted.
  exemptGrantedByUserId: number | null; // Who granted exemption.
  requiredByPolicy: boolean; // Required by policy.
  isBlocked: boolean; // Temporarily blocked.
  failedAttempts: number; // Failed TOTP attempts.
}

// Payload sent by admin to grant TOTP exemption.
export interface TotpAdminGrantExemptPayload {
  permanent: boolean; // If true, exemption does not expire.
  exemptionDays?: number; // Days exempted (if not permanent).
  reason: string; // Audit note for exemption (required).
}

// Response for successful POST admin exemption grant.
export interface TotpAdminGrantExemptResponse {
  success: boolean; // Was the grant successful?
  userId: number;
  exempt: boolean;
  permanent: boolean;
  exemptUntil: string | null; // Expiration (if not permanent).
  reason: string | null; // Grant reason.
  message: string; // For display/logging.
}

// Response for revoking admin TOTP exemption (DELETE).
export interface TotpAdminRevokeExemptResponse {
  success: boolean;
  userId: number;
  exempt: boolean; // Exemption status after revocation.
  permanent: boolean;
  exemptUntil: string | null;
  reason: string | null;
  message: string; // For display/logging.
}

// Admin disables TOTP for a user.
export interface TotpAdminDisableResponse {
  success: boolean;
  userId: number;
  message: string;
}

// --- Login Token management endpoints ---

// Individual login token row.
export interface LoginToken {
  id: number; // Token ID.
  userId: number; // ID of user who owns the token.
  label: string; // User-friendly label for token.
  status: 'Enabled' | 'Disabled'; // Status indicator.
  expiresAt: number; // UNIX timestamp expiration.
  createdAt: number; // UNIX timestamp created.
  createdByUserId: number; // ID of creator/admin.
  lastUsedAt: number; // Last use UNIX timestamp.
  lastUsedIp: string | null; // Last client IP.
  isExpired: boolean; // Is the token expired now?
}

// Payload to create a new login token for a user.
export interface CreateLoginTokenPayload {
  userId: number; // Associate to this user.
  label: string; // Human-friendly label.
  expirationDays?: number; // How long before token expires.
}

// Response after creating a new login token for a user.
export interface CreateLoginTokenResponse {
  token: string; // The new token value.
  tokenId: number; // ID of the created token.
  label: string; // Label for display.
  expiresAt: number; // Expiry timestamp.
}

// Payload to update (enable/disable) a login token.
export interface UpdateLoginTokenStatusPayload {
  enabled: boolean; // Desired enabled status.
}

// Payload for ownership transfer between users.
export interface TransferOwnershipPayload {
  sourceUserId: number; // The transferring user.
  targetUserId: number; // New owner.
}

// Response summarizing the result of ownership transfer.
export interface TransferOwnershipResponse {
  filesTransferred: number; // Number of files moved.
  foldersTransferred: number; // Number of folders moved.
  sharesUpdated: number;
  selfSharesRemoved: number;
  tagAssignmentsRemoved: number;
  tagAssignmentsTransferred: number;
  filesForSaleExcluded: number;
  wrapperFolderName: string; // If used, wrapper folder name.
}
